import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventsService } from '../../../services/events/events.service';
import { ConfigService } from 'app/services/config/config.service';
import { LocationCongigGlobal } from 'app/models/config-global-model';
import { AppService } from 'app/services/app/app.service';
import { RestService } from 'app/services/rest/rest.service';
import { Utils } from 'app/components/utils/Utils';
import { ParameterCart } from 'app/models/cart-model';

interface LockerLocationViewModel extends LocationCongigGlobal {
  slug: string;
  addressLine: string;
  cityLine: string;
  latitude?: number;
  longitude?: number;
  markerX?: number;
  markerY?: number;
}



@Component({
  selector: 'app-locker-location',
  templateUrl: './locker-location.page.html',
  styleUrls: ['./locker-location.page.scss'],
})
export class LockerLocationPage implements OnInit {

  locations: LockerLocationViewModel[] = [];
  selectedLocation: LockerLocationViewModel | null = null;
  mapEmbedUrl = '';
  isReserving = false;

  constructor(
    private configService: ConfigService,
    private eventsService: EventsService,
    private appService: AppService,
    private restService: RestService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    const rawLocations = this.configService.settings?.locations || [];
    this.locations = rawLocations.map((location) => this.mapLocation(location));
    this.setMarkerPositions();

    if (this.locations.length === 1) {
      this.reserveLocation(this.locations[0]);
      return;
    }

    this.selectLocation(this.locations[0] || null);
  }

  ngAfterViewInit(){
    this.eventsService.publish('refreshStep', { title: "0/4", nameBt: null });
  }

  ngOnDestroy() {
  }

  selectLocation(location: LockerLocationViewModel | null) {
    this.selectedLocation = location;
    this.mapEmbedUrl = this.buildMapUrl(location);
  }

  reserveLocation(location: LockerLocationViewModel) {
    if (!location || this.isReserving) {
      return;
    }

    const kiosk = location.code || location.name;
    this.isReserving = true;
    this.appService.kiosk = kiosk;
    this.appService.locationId = location.id;

    this.restService.getStockLockers().subscribe({
      next: (stock) => {
        if (stock != undefined) {
          this.appService.setCurrentStock(stock);
        }
        const parameters:ParameterCart = Utils.getParams(this.route.snapshot.queryParamMap);
        this.router.navigate(['/reserve', kiosk, 'locker-type'], { queryParams: parameters });
      },
      error: () => {
        this.isReserving = false;
      },
      complete: () => {
        this.isReserving = false;
      }
    });
  }

  trackByLocation(_index: number, location: LockerLocationViewModel) {
    return location.id || location.code || location.name;
  }

  private mapLocation(location: LocationCongigGlobal): LockerLocationViewModel {
    const slug = this.slugify(location.code || location.name);

    return {
      ...location,
      slug,
      addressLine: location.address || 'Ubicacion disponible en reserva online',
      cityLine: location.city || '',
      latitude: location.latitude,
      longitude: location.longitude
    };
  }

  private setMarkerPositions() {
    const locationsWithCoordinates = this.locations.filter((location) => this.hasCoordinates(location));

    if (locationsWithCoordinates.length === 0) {
      return;
    }

    const latitudes = locationsWithCoordinates.map((location) => location.latitude as number);
    const longitudes = locationsWithCoordinates.map((location) => location.longitude as number);
    const minLat = Math.min(...latitudes);
    const maxLat = Math.max(...latitudes);
    const minLng = Math.min(...longitudes);
    const maxLng = Math.max(...longitudes);
    const latRange = Math.max(maxLat - minLat, 0.01);
    const lngRange = Math.max(maxLng - minLng, 0.01);

    locationsWithCoordinates.forEach((location) => {
      location.markerX = 12 + (((location.longitude as number) - minLng) / lngRange) * 76;
      location.markerY = 88 - (((location.latitude as number) - minLat) / latRange) * 76;
    });
  }

  private buildMapUrl(location: LockerLocationViewModel | null): string {
    const locationsWithCoordinates = this.locations.filter((item) => this.hasCoordinates(item));

    if (locationsWithCoordinates.length === 0) {
      return 'https://www.openstreetmap.org/export/embed.html?bbox=-8.734%2C42.226%2C-8.700%2C42.246&layer=mapnik';
    }

    const latitudes = locationsWithCoordinates.map((item) => item.latitude as number);
    const longitudes = locationsWithCoordinates.map((item) => item.longitude as number);
    const paddingLat = 0.008;
    const paddingLng = 0.012;
    const bbox = [
      Math.min(...longitudes) - paddingLng,
      Math.min(...latitudes) - paddingLat,
      Math.max(...longitudes) + paddingLng,
      Math.max(...latitudes) + paddingLat
    ]
      .map((value) => value.toFixed(6))
      .join('%2C');

    let url = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik`;

    if (location && this.hasCoordinates(location)) {
      url += `&marker=${location.latitude}%2C${location.longitude}`;
    }

    return url;
  }

  hasCoordinates(location: LockerLocationViewModel | null): boolean {
    return Boolean(location && location.latitude != null && location.longitude != null);
  }

  private slugify(value: string): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

}
